import { createClient } from '@supabase/supabase-js';
import type { SupabaseClient } from '@supabase/supabase-js';

let _authClient: SupabaseClient | null = null;

function getAuthClient(): SupabaseClient {
  if (_authClient) {
    return _authClient;
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseKey) {
    throw new Error(
      'Supabase environment variables are not configured. ' +
      'Please ensure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set.'
    );
  }

  _authClient = createClient(supabaseUrl, supabaseKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  });

  return _authClient;
}

export const authClient = new Proxy({} as SupabaseClient, {
  get(_target, prop) {
    const client = getAuthClient();
    const value = (client as any)[prop];
    return typeof value === 'function' ? value.bind(client) : value;
  },
});

export async function signUp(
  email: string,
  password: string,
  firstName?: string,
  lastName?: string,
  invitationCode?: string
) {
  const redirectUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/auth/verify`
    : '';

  const name = firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || '';

  const { data, error } = await authClient.auth.signUp({
    email,
    password,
    options: {
      data: {
        first_name: firstName || '',
        last_name: lastName || '',
        name,
      },
      emailRedirectTo: redirectUrl,
    },
  });

  if (error) throw error;

  if (data.user) {
    const { error: userError } = await authClient
      .schema('core')
      .from('users')
      .insert({
        id: data.user.id,
        email: data.user.email!,
        first_name: firstName || null,
        last_name: lastName || null,
        name,
        status: 'pending_verification',
        email_verified: false,
      });

    if (userError && !userError.message.includes('duplicate')) {
      console.error('Error creating user record:', userError);
    }

    if (invitationCode) {
      try {
        const { InvitationService } = await import('@/platform/services/invitation-service');
        await InvitationService.acceptInvitation(invitationCode, data.user.id);
      } catch (invError) {
        console.error('Error accepting invitation:', invError);
      }
    }
  }

  return data;
}

export async function signIn(email: string, password: string) {
  const { data, error } = await authClient.auth.signInWithPassword({
    email,
    password,
  });

  if (error) throw error;
  return data;
}

export async function signOut() {
  const { error } = await authClient.auth.signOut();
  if (error) throw error;
}

export async function resetPassword(email: string, redirectUrl?: string) {
  const redirect = redirectUrl || (typeof window !== 'undefined' ? `${window.location.origin}/auth/reset-password` : '');
  const { data, error } = await authClient.auth.resetPasswordForEmail(email, {
    redirectTo: redirect,
  });

  if (error) throw error;
  return data;
}

export async function updatePassword(newPassword: string) {
  const { data, error } = await authClient.auth.updateUser({
    password: newPassword,
  });

  if (error) throw error;
  return data;
}

export async function getCurrentUser() {
  const { data: { user }, error } = await authClient.auth.getUser();
  if (error) throw error;
  return user;
}

export async function getSession() {
  const { data: { session }, error } = await authClient.auth.getSession();
  if (error) throw error;
  return session;
}

export function onAuthStateChange(callback: (event: string, session: any) => void) {
  return authClient.auth.onAuthStateChange((event, session) => {
    callback(event, session);
  });
}
